import React, { FC, useState } from 'react';
import Typography from '@mui/material/Typography';
import isBoolean from 'lodash/isBoolean';

import { IEmployeeBalanceDTO } from 'src/api/dtos';
import { CheckFilledIcon, CloseIcon, Popper, SadSmileyFaceIcon, WarningFilledIcon } from 'src/shared/components';
import { EmployeeBenefitStatusEnums } from 'src/shared/enums';
import { useTranslate } from 'src/shared/providers';

const BALANCE_CHECK_ID = 'balance-check-id';

interface Props {
  balance: IEmployeeBalanceDTO;
  shouldShowError: boolean;
  shouldShowBreakdown?: boolean;
  size?: number;
  hideTooltip?: boolean;
}

export const AbsenceBalanceIcon: FC<Props> = ({
  balance,
  shouldShowError,
  shouldShowBreakdown = true,
  size = 16,
  hideTooltip,
}) => {
  const [tooltipEl, setTooltipEl] = useState<HTMLElement | null>(null);

  return (
    <>
      <Popper id={BALANCE_CHECK_ID} anchorEl={tooltipEl}>
        <TooltipContent balance={balance} shouldShowError={shouldShowError} shouldShowBreakdown={shouldShowBreakdown} />
      </Popper>
      <div
        onMouseEnter={!hideTooltip ? (e) => setTooltipEl(e.currentTarget) : undefined}
        onMouseLeave={!hideTooltip ? () => setTooltipEl(null) : undefined}
      >
        <Icon status={balance.status} shouldShowError={shouldShowError} size={size} />
      </div>
    </>
  );
};

const ValidTooltipContent: FC<{ balance: IEmployeeBalanceDTO; shouldShowBreakdown: boolean }> = ({
  balance,
  shouldShowBreakdown,
}) => {
  const { t } = useTranslate();

  const hasEmptyBalance = !balance?.currentBalanceInSeconds && !balance?.expectedBalanceInSeconds;

  const message = hasEmptyBalance
    ? t('absences.validationWasSuccessful')
    : t('absences.youHaveASufficientBalanceToSubmitThisAbsenceRequest');

  return (
    <div className="d-flex align-items-baseline justify-content-start">
      <div className="me-3">
        <CheckFilledIcon color="#83B72C" size={28} />
      </div>
      <div>
        <h4 className="fw-500">{t('absences.congratulations')}</h4>
        <p>{message}</p>
        {isBoolean(balance.isScheduleConfident) && !balance.isScheduleConfident && (
          <>
            <hr />
            <p>{t('absences.insufficientSchedulingInformation')}</p>
          </>
        )}
        {!hasEmptyBalance && shouldShowBreakdown && (
          <Typography fontSize="0.75rem" color="text.secondary" paddingTop="0.5rem">
            {t('absences.viewBreakdownBelowForMoreInfo')}
          </Typography>
        )}
      </div>
    </div>
  );
};

const ViolatedTooltipContent: FC<{ balance: IEmployeeBalanceDTO }> = ({ balance }) => {
  const { t } = useTranslate();

  const warningMessage =
    balance.expectedBalanceInSeconds < 0
      ? t('absences.althoughYouReAbleToSubmitThisRequestYourBalanceIs')
      : t('absences.youDonTHaveAnEnoughLeftOnYourBalanceTo');

  const defaultMessage = balance.errorMessage ? balance.errorMessage : warningMessage;

  const message =
    isBoolean(balance.isScheduleConfident) && !balance.isScheduleConfident
      ? t('absences.insufficientSchedulingInformation')
      : defaultMessage;

  return (
    <div className="d-flex align-items-baseline justify-content-start">
      <div className="me-3">
        <SadSmileyFaceIcon size={28} />
      </div>
      <div>
        <h4 className="fw-500">{t('global.uhOh')}</h4>
        <p className="mb-0">{message}</p>
        <Typography fontSize="0.75rem" color="text.secondary" paddingTop="0.5rem">
          {t('absences.viewBreakdownBelowForMoreInfo')}
        </Typography>
      </div>
    </div>
  );
};

const FutureViolatedTooltipContent: FC<{ balance: IEmployeeBalanceDTO; shouldShowBreakdown: boolean }> = ({
  balance,
  shouldShowBreakdown,
}) => {
  const { t } = useTranslate();

  const message =
    isBoolean(balance.isScheduleConfident) && !balance.isScheduleConfident
      ? t('absences.insufficientSchedulingInformation')
      : t('absences.althoughYouCurrentlyHaveASufficientBalanceThisRequestMakes');

  return (
    <div className="d-flex align-items-baseline justify-content-start">
      <div className="me-3">
        <WarningFilledIcon className="text-warning" size={28} />
      </div>
      <div>
        <h4 className="fw-500">{t('global.headsUp')}</h4>
        <p className="mb-0">{message}</p>
        {shouldShowBreakdown && (
          <Typography fontSize="0.75rem" color="text.secondary" paddingTop="0.5rem">
            {t('absences.viewBreakdownBelowForMoreInfo')}
          </Typography>
        )}
      </div>
    </div>
  );
};

const TooltipContent: FC<{ balance: IEmployeeBalanceDTO; shouldShowError: boolean; shouldShowBreakdown: boolean }> = ({
  balance,
  shouldShowError,
  shouldShowBreakdown,
}) => {
  switch (balance.status) {
    case EmployeeBenefitStatusEnums.VALID:
      return <ValidTooltipContent balance={balance} shouldShowBreakdown={shouldShowBreakdown} />;
    case EmployeeBenefitStatusEnums.VIOLATED:
      return shouldShowError ? (
        <ViolatedTooltipContent balance={balance} />
      ) : (
        <FutureViolatedTooltipContent balance={balance} shouldShowBreakdown={shouldShowBreakdown} />
      );
    case EmployeeBenefitStatusEnums.FUTURE_VIOLATED:
    default:
      return <FutureViolatedTooltipContent balance={balance} shouldShowBreakdown={shouldShowBreakdown} />;
  }
};

const Icon: FC<{ status: EmployeeBenefitStatusEnums; shouldShowError: boolean; size: number }> = ({
  status,
  shouldShowError,
  size,
}) => {
  switch (status) {
    case EmployeeBenefitStatusEnums.VALID:
      return <CheckFilledIcon aria-describedby={BALANCE_CHECK_ID} color="#83B72C" size={size} />;
    case EmployeeBenefitStatusEnums.VIOLATED:
      return shouldShowError ? (
        <CloseIcon aria-describedby={BALANCE_CHECK_ID} className="text-danger" size={size} />
      ) : (
        <WarningFilledIcon aria-describedby={BALANCE_CHECK_ID} className="text-warning" size={size} />
      );
    case EmployeeBenefitStatusEnums.FUTURE_VIOLATED:
    default:
      return <WarningFilledIcon aria-describedby={BALANCE_CHECK_ID} className="text-warning" size={size} />;
  }
};
